/**
 * orchardFrostBridge - TypeScript bridge for Orchard FROST WASM operations.
 *
 * Supports two backends:
 *   1. Legacy standalone `orchard-frost-wasm` (for backwards compatibility)
 *   2. Unified `frozt-wasm` which includes both Sapling and Orchard FROST
 *
 * The frozt-wasm path provides FroztOrchardSignSession for relay-based signing
 * and the key/address derivation functions (frozt_orchard_*).
 */

// Cache-busted via `?v=<build>` — see src/utils/assetCacheBust.js for
// why these public/-served WASM modules need a per-build token.
import { withCacheBust } from '../../../../utils/assetCacheBust';

const ORCHARD_WASM_PATH = '/orchard-frost/orchard_frost_wasm_bg.wasm';
const ORCHARD_JS_PATH = '/orchard-frost/orchard_frost_wasm.js';
const FROZTO_BUNDLE_WASM_PATH = '/frozto-bundle/frozto_bundle_wasm_bg.wasm';
const FROZTO_BUNDLE_JS_PATH = '/frozto-bundle/frozto_bundle_wasm.js';

/**
 * Opaque WASM-side DKG participant handle. Round-state secret packages
 * live inside the Rust-owned allocation so coefficients and shares never
 * enter the JS heap between calls. Always call `.free()` when done (even
 * on error) to release the backing memory.
 */
export interface DkgParticipantHandle {
	readonly identifier: string;
	round1(): string;
	round2(round1PackagesJson: string): string;
	round3(round2PackagesJson: string): string;
	free(): void;
}

type DkgParticipantCtor = new (
	identifierHex: string,
	maxSigners: number,
	minSigners: number,
) => DkgParticipantHandle;

interface OrchardFrostWasmModule {
	keygen_with_dealer: (max_signers: number, min_signers: number) => string;
	DkgParticipant?: DkgParticipantCtor;
	commit_nonces: (secret_share_hex: string) => string;
	sign_share: (
		identifier_hex: string,
		secret_share_hex: string,
		public_hex: string,
		group_public_hex: string,
		all_commitments_json: string,
		message_hex: string,
		randomizer_hex: string,
	) => string;
	sign_with_nonces: (
		identifier_hex: string,
		secret_share_hex: string,
		public_hex: string,
		group_public_hex: string,
		nonces_opaque_hex: string,
		all_commitments_json: string,
		message_hex: string,
		randomizer_hex: string,
	) => string;
	generate_randomizer: () => string;
	aggregate: (
		commitments_json: string,
		message_hex: string,
		sig_shares_json: string,
		signer_pubkeys_json: string,
		group_public_hex: string,
		randomizer_hex: string,
	) => string;
	get_group_verifying_key: (group_public_hex: string) => string;
	build_orchard_ufvk_from_frost?: (
		group_public_hex: string,
		orchard_extras_hex: string,
	) => string;
	build_orchard_unified_address_from_frost?: (
		group_public_hex: string,
		orchard_extras_hex: string,
	) => string;
	merge_seed_ufvk_with_frost_orchard?: (
		seed_ufvk: string,
		group_public_hex: string,
		orchard_extras_hex: string,
	) => string;
	merge_seed_unified_address_with_frost_orchard?: (
		seed_unified_address: string,
		group_public_hex: string,
		orchard_extras_hex: string,
	) => string;
	orchard_random_extras?: (group_public_hex: string) => string;
	default: (input: BufferSource) => Promise<void>;
}

/**
 * Merge a seed-derived UFVK with a FROST-derived Orchard receiver.
 *
 * The returned UFVK retains the seed's Sapling and transparent receivers
 * (so the existing wallet's Sapling notes stay scannable and the unified
 * address keeps the same t:/sapling: receivers that exchanges already
 * know) but swaps the Orchard receiver for one whose `ak` equals the
 * FROST `group_public`. That way Orchard FROST signatures verify against
 * the PCZT's FVK without disturbing the non-Orchard wallet state.
 */
export function mergeSeedUfvkWithFrostOrchard(
	seedUfvk: string,
	groupPublicHex: string,
	orchardExtrasHex: string,
): string {
	const wasm = getOrchardFrostWasm();
	if (!wasm?.merge_seed_ufvk_with_frost_orchard) {
		throw new Error('orchard-frost-wasm does not expose merge_seed_ufvk_with_frost_orchard — rebuild and redeploy.');
	}
	return wasm.merge_seed_ufvk_with_frost_orchard(seedUfvk, groupPublicHex, orchardExtrasHex);
}

/** Same as `mergeSeedUfvkWithFrostOrchard` but for a unified address. */
export function mergeSeedUnifiedAddressWithFrostOrchard(
	seedUnifiedAddress: string,
	groupPublicHex: string,
	orchardExtrasHex: string,
): string {
	const wasm = getOrchardFrostWasm();
	if (!wasm?.merge_seed_unified_address_with_frost_orchard) {
		throw new Error('orchard-frost-wasm does not expose merge_seed_unified_address_with_frost_orchard — rebuild and redeploy.');
	}
	return wasm.merge_seed_unified_address_with_frost_orchard(seedUnifiedAddress, groupPublicHex, orchardExtrasHex);
}

/**
 * Generate 64-byte Orchard extras (nk||rivk) as a hex string, using the
 * standalone orchard-frost-wasm helper. This lets us build a valid Orchard
 * UFVK directly from the FROST group_public without needing frozt-wasm.
 *
 * Throws if orchard-frost-wasm is not initialised or lacks the helper.
 */
export function generateOrchardExtras(groupPublicHex: string): string {
	const wasm = getOrchardFrostWasm();
	if (!wasm?.orchard_random_extras) {
		throw new Error('orchard-frost-wasm does not expose orchard_random_extras — rebuild and redeploy the WASM bundle.');
	}
	return wasm.orchard_random_extras(groupPublicHex);
}

/**
 * Orchard's `SpendValidatingKey::from_bytes` requires `ak[31] & 0x80 == 0`
 * (positive y-sign on the compressed Pallas point). FROST/RedPallas keygen
 * produces verifying keys with random y-sign, so roughly half of all
 * keygens yield an `ak` that Orchard rejects with
 * "Failed to generate valid Orchard extras after 64 attempts".
 *
 * Per ZIP 32 the canonical fix is to negate `ak` (and the secret) when the
 * y-sign is wrong; we don't have a WASM helper for negating the FROST
 * shares yet, so we simply re-roll keygen until we land on an
 * Orchard-compatible group_public. With p ≈ 0.5 per attempt, 32 attempts
 * fails with probability ≈ 2^-32.
 *
 * Returns the keygen result plus the matching nk||rivk extras.
 */
export interface OrchardKeygenWithExtras {
	keygen: OrchardKeygenResult;
	orchardExtras: string;
	keygenAttempts: number;
}

export function isOrchardCompatibleGroupPublicHex(groupPublicHex: string): boolean {
	if (typeof groupPublicHex !== 'string' || groupPublicHex.length !== 64) {
		return false;
	}
	const lastByte = parseInt(groupPublicHex.slice(62, 64), 16);
	if (Number.isNaN(lastByte)) return false;
	// ak must not be the all-zero (identity) point and the y-sign bit
	// (top bit of byte 31) must be 0 — see orchard 0.10
	// SpendValidatingKey::from_bytes.
	if ((lastByte & 0x80) !== 0) return false;
	if (/^0+$/.test(groupPublicHex)) return false;
	return true;
}

const DEFAULT_KEYGEN_RETRY_LIMIT = 32;

export function orchardKeygenWithCompatibleExtras(
	maxSigners: number,
	minSigners: number,
	options: { maxAttempts?: number } = {},
): OrchardKeygenWithExtras {
	const wasm = getOrchardFrostWasm();
	if (!wasm) throw new Error('orchard-frost WASM not initialised');
	if (!wasm.orchard_random_extras) {
		throw new Error('orchard-frost-wasm does not expose orchard_random_extras — rebuild and redeploy the WASM bundle.');
	}

	const maxAttempts = Math.max(1, options.maxAttempts ?? DEFAULT_KEYGEN_RETRY_LIMIT);
	let lastError: unknown = null;
	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		const keygen = orchardKeygen(maxSigners, minSigners);
		const groupPublic = keygen.publicKeyPackage.groupPublic;
		if (!isOrchardCompatibleGroupPublicHex(groupPublic)) {
			lastError = new Error(`group_public has wrong y-sign (attempt ${attempt})`);
			continue;
		}
		try {
			const orchardExtras = wasm.orchard_random_extras(groupPublic);
			if (attempt > 1) {
				console.log(
					`[orchard-frost] orchardKeygenWithCompatibleExtras succeeded on attempt ${attempt}/${maxAttempts}`,
				);
			}
			return { keygen, orchardExtras, keygenAttempts: attempt };
		} catch (err) {
			lastError = err;
		}
	}

	const detail = lastError instanceof Error ? lastError.message : String(lastError ?? 'unknown error');
	throw new Error(
		`orchard-frost keygen could not produce an Orchard-compatible ak after ${maxAttempts} attempts: ${detail}`,
	);
}

let wasmModule: OrchardFrostWasmModule | null = null;
let initPromise: Promise<OrchardFrostWasmModule> | null = null;

export async function initOrchardFrostWasm(): Promise<OrchardFrostWasmModule> {
	if (wasmModule) return wasmModule;
	if (initPromise) return initPromise;

	initPromise = (async () => {
		try {
			const jsModule = await import(/* webpackIgnore: true */ withCacheBust(ORCHARD_JS_PATH));
			const wasmResponse = await fetch(withCacheBust(ORCHARD_WASM_PATH));
			const wasmBytes = await wasmResponse.arrayBuffer();
			await jsModule.default(wasmBytes);
			wasmModule = jsModule as unknown as OrchardFrostWasmModule;
			console.log('[orchard-frost] WASM initialised');
			return wasmModule;
		} catch (err) {
			initPromise = null;
			throw new Error('Failed to initialise orchard-frost WASM: ' + (err as Error).message);
		}
	})();

	return initPromise;
}

export function getOrchardFrostWasm(): OrchardFrostWasmModule | null {
	return wasmModule;
}

export function isOrchardFrostReady(): boolean {
	return wasmModule !== null;
}

/**
 * Check if the unified frozt-wasm module is available with Orchard support.
 * This is the preferred path; the legacy standalone module is the fallback.
 */
export function isFroztOrchardAvailable(): boolean {
	try {
		const froztWasm = (window as any).__frozt_wasm;
		return froztWasm?.FroztOrchardSignSession != null;
	} catch {
		return false;
	}
}

export interface OrchardKeyShare {
	id: string;
	secretShare: string;
	publicKey: string;
	groupPublic: string;
}

export interface OrchardPublicKeyPackage {
	signerPubkeys: Record<string, string>;
	groupPublic: string;
}

export interface OrchardKeygenResult {
	shares: Record<string, OrchardKeyShare>;
	publicKeyPackage: OrchardPublicKeyPackage;
}

export interface OrchardKeyBundle {
	myShare: OrchardKeyShare;
	publicKeyPackage: OrchardPublicKeyPackage;
	orchardExtras?: string;
	/**
	 * For threshold (t-of-n) trusted-dealer mode: shares not yet delivered
	 * to other vault devices, keyed by that device's Vultisig localPartyId.
	 * On each cosigner DKG request the initiator looks up the requested
	 * party's share here and removes it from the map once delivered.
	 *
	 * In legacy 2-of-2 mode this field is absent. In proper multi-party
	 * DKG mode it is also absent — each device derives its own share
	 * directly from the ceremony, never holding other parties' shares.
	 */
	pendingShares?: Record<string, OrchardKeyShare>;
	/** Threshold used during keygen. */
	minSigners?: number;
	/** Total shares / participants in the keygen. */
	maxSigners?: number;
	/**
	 * How this bundle was produced. Used by the send path to decide
	 * whether a missing cosigner share should trigger a trusted-dealer
	 * fallback or refuse the sign (a proper-DKG bundle must never be
	 * overwritten by a trusted-dealer regen — that would invalidate
	 * every other vault device's share).
	 *
	 *   - `'trusted-dealer-2of2'` (legacy): 2-of-2, undifferentiated shares
	 *   - `'trusted-dealer-threshold'`: t-of-n via `keygen_with_dealer`
	 *     (dealer temporarily held all shares; `pendingShares` is set)
	 *   - `'proper-dkg'`: Pedersen N-party DKG, no trusted dealer
	 */
	dkgKind?: 'trusted-dealer-2of2' | 'trusted-dealer-threshold' | 'proper-dkg';
	/**
	 * True when the bundle was generated alongside the SDK shares during a
	 * single, local "Create Vault" ceremony — i.e. the per-device shares
	 * never crossed the network and the dealer is the user themselves
	 * generating brand-new shares from a fresh phrase. In this case the
	 * usual "trusted-dealer ⇒ offer DKG upgrade" prompt is misleading:
	 * there is no second device whose trust we'd be improving by re-running
	 * a multi-party ceremony, so the UI treats the bundle as final.
	 */
	bakedAtCreation?: boolean;
	/**
	 * This share's owner — the Vultisig `localPartyId` of the device the
	 * bundle was generated for. Set by `generateOrchardBundlesForFreshVault`
	 * so callers can pick "my" bundle out of the returned array (the Orchard
	 * FROST setup wizard's local-DKG path matches on this, exactly like
	 * Monero's `MoneroKeyShareBundle.partyName`). Transient: it is a match
	 * key for the in-tab ceremony only and is NOT persisted by
	 * `packVultisigOrchardKeyshareBundle`, so a re-imported bundle won't
	 * carry it (the device re-derives its identity from its own vault).
	 */
	partyName?: string;
	/**
	 * Vultisig `localPartyId`s of every participant in the DKG ceremony.
	 * Present for threshold-dealer and proper-DKG bundles; absent for legacy.
	 */
	participantPartyIds?: string[];
	/**
	 * On-disk ceremony version.
	 *
	 *   - undefined / 1 : Legacy bundle. Proper-DKG flows pre-April-2026
	 *     generated `orchardExtras` locally on each device, so any
	 *     proper-DKG bundle without version 2 almost certainly has
	 *     divergent nk‖rivk across devices (same group_public, different
	 *     UFVK) and MUST be rebuilt via a fresh ceremony before the
	 *     wallet can receive spendable Orchard notes.
	 *   - 2 : The initiator broadcasts shared `orchardExtras` in the
	 *     post-round-3 `dkg-extras` message; every device persists the
	 *     same nk‖rivk verbatim.
	 */
	ceremonyVersion?: number;
	/**
	 * Zcash block height at which this Orchard bundle was created. Vultisig
	 * frozto stores this in its native KeyShareBundle so scanners can avoid
	 * walking blocks before the wallet existed.
	 */
	birthday?: number;
	/** Native Vultisig frozto bundle format version, when imported from .vult. */
	vultisigKeyshareBundleVersion?: number;
}

export const CURRENT_ORCHARD_BUNDLE_VERSION = 2;

// ── Frozt-wasm Orchard key derivation (unified module) ──────────────

interface FroztWasmOrchardKeys {
	frozt_orchard_generate_extras: () => Uint8Array;
	frozt_orchard_derive_extras_from_seed: (seed: Uint8Array, accountIndex: number) => Uint8Array;
	frozt_orchard_build_fvk: (pubKeyPackage: Uint8Array, orchardExtras: Uint8Array) => Uint8Array;
	frozt_orchard_build_unified_address: (pubKeyPackage: Uint8Array, orchardExtras: Uint8Array, diversifierIndex: bigint) => string;
	frozt_build_orchard_ufvk: (pubKeyPackage: Uint8Array, orchardExtras: Uint8Array) => string;
	frozt_build_combined_ufvk: (saplingPkp: Uint8Array, saplingExtras: Uint8Array, orchardPkp: Uint8Array, orchardExtras: Uint8Array) => string;
	frozt_build_combined_unified_address: (saplingPkp: Uint8Array, saplingExtras: Uint8Array, orchardPkp: Uint8Array, orchardExtras: Uint8Array) => string;
}

function getFroztOrchardWasm(): FroztWasmOrchardKeys | null {
	try {
		const mod = (window as any).__frozt_wasm;
		if (mod?.frozt_orchard_generate_extras) return mod as FroztWasmOrchardKeys;
	} catch { /* not available */ }
	return null;
}

export function hasFroztOrchardKeys(): boolean {
	return getFroztOrchardWasm() !== null;
}

function hexToU8(hex: string): Uint8Array {
	const clean = hex.replace(/^0x/i, '');
	const bytes = new Uint8Array(clean.length / 2);
	for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(clean.substr(i * 2, 2), 16);
	return bytes;
}

function u8ToHex(bytes: Uint8Array): string {
	return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

export function orchardGenerateExtras(): string {
	const wasm = getFroztOrchardWasm();
	if (!wasm) throw new Error('frozt-wasm Orchard keys module not available');
	return u8ToHex(wasm.frozt_orchard_generate_extras());
}

export function orchardDeriveExtrasFromSeed(seed: Uint8Array, accountIndex: number): string {
	const wasm = getFroztOrchardWasm();
	if (!wasm) throw new Error('frozt-wasm Orchard keys module not available');
	return u8ToHex(wasm.frozt_orchard_derive_extras_from_seed(seed, accountIndex));
}

/**
 * Derive a unified Orchard address from FROST public key package + extras.
 * Returns an encoded unified address string (mainnet).
 */
export function orchardDeriveUnifiedAddress(
	pubKeyPackageBytes: Uint8Array,
	orchardExtrasBytes: Uint8Array,
	diversifierIndex = 0n,
): string {
	const wasm = getFroztOrchardWasm();
	if (!wasm) throw new Error('frozt-wasm Orchard keys module not available');
	return wasm.frozt_orchard_build_unified_address(pubKeyPackageBytes, orchardExtrasBytes, diversifierIndex);
}

/**
 * Derive an Orchard-only UFVK string for scanner integration.
 */
export function orchardDeriveUfvk(
	pubKeyPackageBytes: Uint8Array,
	orchardExtrasBytes: Uint8Array,
): string {
	const wasm = getFroztOrchardWasm();
	if (!wasm) throw new Error('frozt-wasm Orchard keys module not available');
	return wasm.frozt_build_orchard_ufvk(pubKeyPackageBytes, orchardExtrasBytes);
}

/**
 * Build a combined Sapling+Orchard UFVK for scanner integration.
 */
export function buildCombinedUfvk(
	saplingPkpBytes: Uint8Array,
	saplingExtrasBytes: Uint8Array,
	orchardPkpBytes: Uint8Array,
	orchardExtrasBytes: Uint8Array,
): string {
	const wasm = getFroztOrchardWasm();
	if (!wasm) throw new Error('frozt-wasm Orchard keys module not available');
	return wasm.frozt_build_combined_ufvk(saplingPkpBytes, saplingExtrasBytes, orchardPkpBytes, orchardExtrasBytes);
}

/**
 * Derive address + UFVK from an OrchardKeyBundle that includes extras.
 * Falls back to null if extras are missing or neither WASM path is available.
 *
 * Prefers the `orchard-frost-wasm` helpers (`build_orchard_*_from_frost`)
 * because they take the 32-byte FROST `group_public` directly as `ak`. The
 * legacy `frozt-wasm` helpers expect a full `frost_core` serialised
 * PublicKeyPackage and will silently throw when given just the 32-byte
 * group_public the bundle actually stores — falling back here to a seed-
 * derived UFVK, which then disagrees with the FROST signer's ak and breaks
 * Orchard signing (SighashMismatch / InvalidSignature).
 */
export function deriveOrchardAddressFromBundle(bundle: OrchardKeyBundle): {
	unifiedAddress: string; ufvk: string;
} | null {
	const groupPublicHex = bundle.publicKeyPackage.groupPublic;

	// Backfill: older bundles created before `orchard-frost-wasm` could
	// generate its own extras ended up with `orchardExtras: undefined`.
	// That silently fell back to seed-derived UFVKs whose `ak` did NOT
	// match the FROST group_public, causing every shielded send to fail
	// with "Orchard FROST group_public mismatch" at apply-orchard-frost.
	// Generate fresh extras deterministically-stable-per-session by
	// mutating the bundle in place and trusting the caller to persist it.
	if (!bundle.orchardExtras) {
		try {
			bundle.orchardExtras = generateOrchardExtras(groupPublicHex);
			console.warn(
				`[orchard-frost] Bundle had no orchardExtras — generated fresh 64-byte nk||rivk for group_public=${groupPublicHex.slice(0, 16)}... Caller should persist bundle to avoid regenerating (different extras = different UA, but ak stays stable so FROST signing still works).`,
			);
		} catch (err) {
			console.error('[orchard-frost] Could not backfill orchardExtras:', (err as Error).message);
			return null;
		}
	}

	// Preferred path: derive directly from FROST group_public + extras.
	// The group_public from the DKG is already the 32-byte serialised `ak`,
	// so `ak || nk || rivk` trivially produces the Orchard FVK that the
	// PCZT will embed and verify against.
	const orchardWasm = getOrchardFrostWasm();
	if (orchardWasm?.build_orchard_ufvk_from_frost && orchardWasm?.build_orchard_unified_address_from_frost) {
		try {
			const ufvk = orchardWasm.build_orchard_ufvk_from_frost(groupPublicHex, bundle.orchardExtras);
			const unifiedAddress = orchardWasm.build_orchard_unified_address_from_frost(groupPublicHex, bundle.orchardExtras);
			console.log(
				`[orchard-frost] FROST-native UFVK derived (group_public=${groupPublicHex.slice(0, 16)}...) → ufvk=${ufvk.slice(0, 20)}..., ua=${unifiedAddress.slice(0, 20)}...`,
			);
			return { unifiedAddress, ufvk };
		} catch (err) {
			console.error('[orchard-frost] FROST-native address derivation threw — FALLING BACK WILL CAUSE group_public MISMATCH:', (err as Error).message);
			// Intentional fall-through only because the legacy path is a
			// strict improvement — it returns the same value when it works.
		}
	} else {
		console.warn('[orchard-frost] build_orchard_{ufvk,unified_address}_from_frost are not available in the loaded WASM module — rebuild orchard-frost-wasm and redeploy to /orchard-frost/.');
	}

	// Legacy path via frozt-wasm. NOTE: this takes a serialised PKP but the
	// bundle only stores the 32-byte group_public; the call will throw for
	// any non-32-byte deserialisation but accepts 32 bytes directly after
	// the frozt-lib fix in orchard_keys.rs. Kept for backwards compat.
	const wasm = getFroztOrchardWasm();
	if (wasm) {
		try {
			const pkpBytes = hexToU8(groupPublicHex);
			const extrasBytes = hexToU8(bundle.orchardExtras);
			const unifiedAddress = orchardDeriveUnifiedAddress(pkpBytes, extrasBytes);
			const ufvk = orchardDeriveUfvk(pkpBytes, extrasBytes);
			console.log(
				`[orchard-frost] legacy frozt-wasm UFVK derived (group_public=${groupPublicHex.slice(0, 16)}...) → ufvk=${ufvk.slice(0, 20)}..., ua=${unifiedAddress.slice(0, 20)}...`,
			);
			return { unifiedAddress, ufvk };
		} catch (err) {
			console.error('[orchard-frost] legacy address derivation failed:', (err as Error).message);
		}
	}

	console.error(
		`[orchard-frost] Could not derive FROST UFVK — both WASM paths unavailable or errored. PCZT builds will use seed-derived keys and Orchard FROST signatures will not verify.`,
	);
	return null;
}

export function orchardKeygen(
	maxSigners: number,
	minSigners: number,
): OrchardKeygenResult {
	const wasm = getOrchardFrostWasm();
	if (!wasm) throw new Error('orchard-frost WASM not initialised');

	const resultJson = wasm.keygen_with_dealer(maxSigners, minSigners);
	const result = JSON.parse(resultJson);

	const shares: Record<string, OrchardKeyShare> = {};
	for (const [idHex, shareData] of Object.entries(result.shares as Record<string, any>)) {
		shares[idHex] = {
			id: shareData.id,
			secretShare: shareData.secret_share,
			publicKey: shareData.public,
			groupPublic: shareData.group_public,
		};
	}

	return {
		shares,
		publicKeyPackage: {
			signerPubkeys: result.public_key_package.signer_pubkeys,
			groupPublic: result.public_key_package.group_public,
		},
	};
}

/**
 * Pedersen 3-round DKG (no trusted dealer). Creates a participant bound
 * to this device's `identifierHex` (typically derived from its Vultisig
 * `localPartyId`); round state lives WASM-side until `.free()` is
 * called, so never leak this handle across React renders — always own
 * it inside a single async ceremony runner.
 *
 * Typical flow:
 *   const me = createDkgParticipant(myIdHex, N, T);
 *   try {
 *     const r1 = me.round1();             // broadcast to all others
 *     // ...collect r1 packages from others into { id: pkg } object...
 *     const r2Outgoing = me.round2(JSON.stringify(r1Received));
 *     // ...send each value in r2Outgoing P2P to its keyed recipient,
 *     //    collect round2 packages addressed to me from others...
 *     const result = me.round3(JSON.stringify(r2ForMe));
 *     // result = { share: OrchardKeyShare, public_key_package }
 *   } finally { me.free(); }
 *
 * Throws if the WASM bundle does not expose `DkgParticipant` (i.e. an
 * old build is deployed).
 */
export function createDkgParticipant(
	identifierHex: string,
	maxSigners: number,
	minSigners: number,
): DkgParticipantHandle {
	const wasm = getOrchardFrostWasm();
	if (!wasm) throw new Error('orchard-frost WASM not initialised');
	if (!wasm.DkgParticipant) {
		throw new Error(
			'orchard-frost WASM does not expose DkgParticipant — rebuild and redeploy /public/orchard-frost/.',
		);
	}
	if (minSigners < 2) throw new Error('minSigners must be >= 2');
	if (maxSigners < minSigners) throw new Error('maxSigners must be >= minSigners');
	return new wasm.DkgParticipant(identifierHex, maxSigners, minSigners);
}

/**
 * Result of a successful 3-round DKG from this party's perspective.
 */
export interface DkgOutcome {
	share: OrchardKeyShare;
	publicKeyPackage: OrchardPublicKeyPackage;
}

/**
 * Parse the JSON returned by `DkgParticipant.round3()` into the canonical
 * bundle shape used by the rest of the app.
 */
export function parseDkgRound3Result(jsonStr: string): DkgOutcome {
	const parsed = JSON.parse(jsonStr) as {
		share: {
			id: string;
			secret_share: string;
			public: string;
			group_public: string;
		};
		public_key_package: {
			signer_pubkeys: Record<string, string>;
			group_public: string;
		};
	};
	return {
		share: {
			id: parsed.share.id,
			secretShare: parsed.share.secret_share,
			publicKey: parsed.share.public,
			groupPublic: parsed.share.group_public,
		},
		publicKeyPackage: {
			signerPubkeys: parsed.public_key_package.signer_pubkeys,
			groupPublic: parsed.public_key_package.group_public,
		},
	};
}

/**
 * Whether the currently loaded orchard-frost WASM supports proper (no-
 * trusted-dealer) DKG. When false, callers should either fall back to
 * the 2-of-2 trusted-dealer path or refuse to start a ceremony and
 * surface an error to the user asking them to update the WASM bundle.
 */
export function isProperDkgAvailable(): boolean {
	return getOrchardFrostWasm()?.DkgParticipant != null;
}

export interface NonceCommitment {
	noncesOpaque: string;
	commitments: string;
}

export function orchardCommitNonces(secretShareHex: string): NonceCommitment {
	const wasm = getOrchardFrostWasm();
	if (!wasm) throw new Error('orchard-frost WASM not initialised');

	const resultJson = wasm.commit_nonces(secretShareHex);
	const result = JSON.parse(resultJson);
	return {
		noncesOpaque: result.nonces_opaque,
		commitments: result.commitments,
	};
}

export function orchardSignShare(
	identifierHex: string,
	secretShareHex: string,
	publicHex: string,
	groupPublicHex: string,
	allCommitmentsJson: string,
	messageHex: string,
	randomizerHex: string,
): string {
	const wasm = getOrchardFrostWasm();
	if (!wasm) throw new Error('orchard-frost WASM not initialised');

	return wasm.sign_share(
		identifierHex,
		secretShareHex,
		publicHex,
		groupPublicHex,
		allCommitmentsJson,
		messageHex,
		randomizerHex,
	);
}

/**
 * Sign using pre-committed nonces (proper two-round FROST).
 * Both parties must use the SAME commitments in allCommitmentsJson.
 * Returns the bare 32-byte signature share as a hex string.
 */
export function orchardSignWithNonces(
	identifierHex: string,
	secretShareHex: string,
	publicHex: string,
	groupPublicHex: string,
	noncesOpaqueHex: string,
	allCommitmentsJson: string,
	messageHex: string,
	randomizerHex: string,
): string {
	const wasm = getOrchardFrostWasm();
	if (!wasm) throw new Error('orchard-frost WASM not initialised');

	return wasm.sign_with_nonces(
		identifierHex,
		secretShareHex,
		publicHex,
		groupPublicHex,
		noncesOpaqueHex,
		allCommitmentsJson,
		messageHex,
		randomizerHex,
	);
}

/**
 * Generate a random 32-byte scalar suitable for use as a FROST randomizer.
 */
export function orchardGenerateRandomizer(): string {
	const wasm = getOrchardFrostWasm();
	if (!wasm) throw new Error('orchard-frost WASM not initialised');
	return wasm.generate_randomizer();
}

export function orchardAggregate(
	commitmentsJson: string,
	messageHex: string,
	sigSharesJson: string,
	signerPubkeysJson: string,
	groupPublicHex: string,
	randomizerHex: string,
): string {
	const wasm = getOrchardFrostWasm();
	if (!wasm) throw new Error('orchard-frost WASM not initialised');

	return wasm.aggregate(
		commitmentsJson,
		messageHex,
		sigSharesJson,
		signerPubkeysJson,
		groupPublicHex,
		randomizerHex,
	);
}

export function orchardGroupVerifyingKey(groupPublicHex: string): string {
	const wasm = getOrchardFrostWasm();
	if (!wasm) throw new Error('orchard-frost WASM not initialised');
	return wasm.get_group_verifying_key(groupPublicHex);
}

interface FroztoBundleWasmModule {
	pack_orchard_keyshare_bundle: (
		identifierHex: string,
		secretShareHex: string,
		publicHex: string,
		groupPublicHex: string,
		signerPubkeysJson: string,
		orchardExtrasHex: string,
		minSigners: number,
		birthday: number,
	) => string;
	unpack_orchard_keyshare_bundle: (bundleHex: string) => string;
	default: (input: BufferSource) => Promise<void>;
}

let froztoBundleModule: FroztoBundleWasmModule | null = null;
let froztoBundleInitPromise: Promise<FroztoBundleWasmModule> | null = null;

async function initFroztoBundleWasm(): Promise<FroztoBundleWasmModule> {
	if (froztoBundleModule) return froztoBundleModule;
	if (froztoBundleInitPromise) return froztoBundleInitPromise;

	froztoBundleInitPromise = (async () => {
		try {
			const jsModule = await import(/* webpackIgnore: true */ FROZTO_BUNDLE_JS_PATH);
			const wasmResponse = await fetch(FROZTO_BUNDLE_WASM_PATH);
			const wasmBytes = await wasmResponse.arrayBuffer();
			await jsModule.default(wasmBytes);
			froztoBundleModule = jsModule as unknown as FroztoBundleWasmModule;
			return froztoBundleModule;
		} catch (err) {
			froztoBundleInitPromise = null;
			throw new Error('Failed to initialise frozto bundle WASM: ' + (err as Error).message);
		}
	})();

	return froztoBundleInitPromise;
}

/**
 * Mirrors `getSmartOrchardBirthdayHeight()` in orchardFrostVaultIntegration.ts
 * without creating a circular import. When a bundle's stored `birthday`
 * is 0 / missing, we fall back to the most recent tip we cached, or the
 * NU6 mainnet height as a final safety net. NEVER 0 — that forces a
 * full chain rescan when the bundle is later re-imported.
 */
const ORCHARD_FROST_BIRTHDAY_FALLBACK = 2726400;
function resolveSmartBirthdayHeight(stored?: number | null): number {
	const value = Math.floor(Number(stored) || 0);
	if (value >= ORCHARD_FROST_BIRTHDAY_FALLBACK) return value;
	if (typeof localStorage !== 'undefined') {
		try {
			const cached = Number(localStorage.getItem('wb32:orchard-frost:last-known-tip') || 0);
			if (Number.isFinite(cached) && cached >= ORCHARD_FROST_BIRTHDAY_FALLBACK) {
				return Math.floor(cached);
			}
		} catch { /* ignore */ }
	}
	return ORCHARD_FROST_BIRTHDAY_FALLBACK;
}

function hexToBase64(hex: string): string {
	const clean = hex.replace(/^0x/i, '');
	let bin = '';
	for (let i = 0; i < clean.length; i += 2) {
		bin += String.fromCharCode(parseInt(clean.slice(i, i + 2), 16));
	}
	return btoa(bin);
}

function base64ToHex(b64: string): string {
	const bin = atob(b64);
	let out = '';
	for (let i = 0; i < bin.length; i++) {
		out += bin.charCodeAt(i).toString(16).padStart(2, '0');
	}
	return out;
}

export async function packVultisigOrchardKeyshareBundle(bundle: OrchardKeyBundle): Promise<string> {
	if (!bundle.orchardExtras) {
		throw new Error('Cannot pack Vultisig Orchard bundle without orchardExtras.');
	}
	if (!bundle.minSigners) {
		throw new Error('Cannot pack Vultisig Orchard bundle without minSigners.');
	}
	const wasm = await initFroztoBundleWasm();
	const birthday = Math.max(resolveSmartBirthdayHeight(bundle.birthday), 0);
	const hex = wasm.pack_orchard_keyshare_bundle(
		bundle.myShare.id,
		bundle.myShare.secretShare,
		bundle.myShare.publicKey,
		bundle.publicKeyPackage.groupPublic,
		JSON.stringify(bundle.publicKeyPackage.signerPubkeys),
		bundle.orchardExtras,
		bundle.minSigners,
		birthday,
	);
	return hexToBase64(hex);
}

export async function unpackVultisigOrchardKeyshareBundle(bundleBase64: string): Promise<OrchardKeyBundle> {
	const wasm = await initFroztoBundleWasm();
	const parsed = JSON.parse(wasm.unpack_orchard_keyshare_bundle(base64ToHex(bundleBase64))) as OrchardKeyBundle;
	return {
		...parsed,
		dkgKind: parsed.dkgKind || 'proper-dkg',
		ceremonyVersion: CURRENT_ORCHARD_BUNDLE_VERSION,
	};
}
